import { Eyebrow } from "hec/components/ui/eyebrow";
import { useHasInnerText } from "hec/hooks/useHasInnerText";
import type { CSSProperties } from "react";

import { builderImageUrl } from "~/hec/utils/builder-image-url";
import { sanitizeHtml } from "~/utils/sanitize-html";

import type { TextBlockProps } from "./types";

const TextBlock = ({
  eyebrowType,
  eyebrowIcon,
  eyebrow,
  headlineType,
  headlineImage,
  headline,
  supportText,
  couponCode,
  enableCouponCode,
  eyebrowBackgroundColor,
  eyebrowTextColor,
  cta,
  buttonColor,
  overlay,
  eyebrowSize,
  disclaimer,
  link,
  alignment,
  overlayColor,
  textBlock,
  buttonTextColor,
}: TextBlockProps) => {
  const hasHeadlineContent = useHasInnerText(headline ?? "");
  const hasSupportText = useHasInnerText(supportText ?? "");
  const hasTextBlock = useHasInnerText(textBlock ?? "");
  const hasDisclaimer = useHasInnerText(disclaimer ?? "");

  let overlayBgStyle: CSSProperties | undefined = undefined;
  if (overlay) {
    overlayBgStyle = {
      backgroundColor: overlayColor || "rgba(0,0,0,0.25)",
      opacity: 0.25,
      position: "absolute",
      inset: 0,
      borderRadius: 24,
      zIndex: 0,
    };
  }

  const alignmentClass =
    alignment === "center"
      ? "text-center"
      : alignment === "right"
        ? "text-right"
        : "text-left";

  const containerJustifyClass =
    alignment === "center"
      ? "items-center"
      : alignment === "right"
        ? "items-end"
        : "items-start";

  return (
    <div className="text-block-container flex max-w-[350px] flex-col justify-center gap-[20px] md:max-w-[400px] lg:min-w-[400px] lg:max-w-[600px]">
      <div className="text-block-content relative flex flex-col gap-[16px] self-stretch overflow-hidden rounded-[24px] p-[40px]">
        {overlay && <div style={overlayBgStyle} />}
        <div
          className={`relative z-[8] flex w-full flex-col gap-[16px] ${containerJustifyClass}`}
        >
          <div className="flex flex-col gap-2">
            {eyebrowType === "none" ? null : eyebrow &&
              eyebrowType !== "icon" ? (
              <Eyebrow
                size={eyebrowSize}
                tag="div"
                data-testid="eyebrow"
                type={eyebrowType}
                eyebrowBackgroundColor={eyebrowBackgroundColor}
                textColor={eyebrowTextColor}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(eyebrow) }}
              />
            ) : (
              <img
                src={builderImageUrl(eyebrowIcon, {
                  width: 96,
                  format: "webp",
                })}
                alt="Eyebrow Icon"
                className="mr-2 inline-block h-[48px] w-[48px]"
                width={48}
                height={48}
                loading="lazy"
              />
            )}
          </div>
          {hasHeadlineContent && headlineType === "text" && headline ? (
            <div className="text-block-content__headline flex gap-[77px]">
              <p
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(headline) }}
                className={`font-grotesque text-[30px] font-normal not-italic leading-[110%] tracking-[-0.48px] md:text-[40px] lg:text-[48px] ${alignmentClass}`}
              />
            </div>
          ) : headlineType === "image" && headlineImage ? (
            <img
              src={builderImageUrl(headlineImage, {
                width: 600,
                format: "webp",
              })}
              alt="Headline"
              className="inline-block h-auto w-auto max-w-[250px] lg:max-w-[300px]"
              sizes="(min-width: 1024px) 300px, 250px"
              loading="lazy"
            />
          ) : null}
          {hasSupportText && supportText && (
            <div className="text-block-content__support-text flex gap-[77px]">
              <p
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(supportText) }}
                className={`body-lg lg:body-xl font-grotesque font-normal not-italic leading-[150%] tracking-[0] ${alignmentClass}`}
              />
            </div>
          )}
          {hasTextBlock && textBlock && (
            <div className="text-block-content__support-text flex gap-[77px] self-stretch">
              <p
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(textBlock) }}
                className={`body-lg lg:body-xl font-grotesque font-normal not-italic leading-[150%] tracking-[0] ${alignmentClass}`}
              />
            </div>
          )}
          {enableCouponCode && couponCode && (
            <div
              className={`text-block-content__coupon-code body-lg ${alignmentClass}`}
            >
              Coupon Code: <span>CODE-{couponCode}</span>
            </div>
          )}
          <div
            className={`text-block-content__cta flex ${
              alignment === "center"
                ? "justify-center"
                : alignment === "right"
                  ? "justify-end"
                  : "justify-start"
            }`}
          >
            {cta && link && (
              <a
                href={link}
                className="inline-flex items-center justify-center rounded-full px-6 py-3 font-medium transition-colors"
                style={{ backgroundColor: buttonColor, color: buttonTextColor }}
              >
                {cta}
              </a>
            )}
          </div>
          {hasDisclaimer && disclaimer && (
            <div
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(disclaimer) }}
              className={`text-block-content__disclaimer mb-6 font-grotesque font-light not-italic leading-tight tracking-[0] text-black ${alignmentClass}`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TextBlock;
